import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { getProductImageUrl } from "../../../utils/imageUrl";

const ExploreLoader = memo(function ExploreLoader() {
	return (
		<section
			className="flex min-h-[320px] w-full flex-col items-center justify-center gap-4 bg-white px-4 py-16"
			aria-live="polite"
			aria-busy="true"
		>
			<div
				className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[#713580]"
				aria-hidden="true"
			/>
			<p className="font-poppins text-base font-medium text-gray-700">
				Loading latest Risky collection...
			</p>
		</section>
	);
});

function ExploreCollection({
	data = [],
	loading = false,
	error = "",
	onRetry,
}) {
	const filteredData = useMemo(() => {
		if (!Array.isArray(data)) {
			return [];
		}

		return data
			.filter((item) => item?.viewInfront === true && getProductImageUrl(item))
			.slice(0, 4);
	}, [data]);

	const hasData = Array.isArray(data) && data.length > 0;

	// Show a loader only while a real request is in progress.
	if (loading && !hasData) {
		return <ExploreLoader />;
	}

	// Never leave the page in an endless loader when the request fails.
	if (error && !hasData) {
		return (
			<section className="flex min-h-[300px] items-center justify-center bg-white px-4 py-16">
				<div className="w-full max-w-xl rounded-xl border border-amber-300 bg-amber-50 p-6 text-center shadow-sm">
					<p className="font-poppins text-base font-semibold text-gray-800">
						{error}
					</p>
					{typeof onRetry === "function" && (
						<button
							type="button"
							onClick={onRetry}
							className="mt-5 rounded-md bg-[#315a9e] px-6 py-3 font-poppins font-semibold text-white transition hover:bg-[#274a84] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-200"
						>
							Try Again
						</button>
					)}
				</div>
			</section>
		);
	}

	// The request finished successfully but there are no products.
	if (!hasData) {
		return (
			<section className="flex min-h-[260px] items-center justify-center bg-white px-4 py-16">
				<p className="font-poppins text-base text-gray-600">
					No Risky products are available right now.
				</p>
			</section>
		);
	}

	if (filteredData.length === 0) {
		return (
			<section className="flex min-h-[260px] items-center justify-center bg-white px-4 py-16">
				<p className="font-poppins text-base text-gray-600">
					No featured Risky collection is available right now.
				</p>
			</section>
		);
	}

	return (
		<section
			className="flex flex-col items-center justify-center bg-white px-4 py-20"
			aria-labelledby="match-needs-title"
		>
			<div className="mb-12 text-center">
				<h2
					id="match-needs-title"
					className="font-lora text-3xl font-semibold text-gray-800 sm:text-4xl lg:text-5xl"
				>
					Match your needs with the best
				</h2>
				<p className="mt-3 text-base text-gray-600 sm:text-lg">
					Explore our exclusive collection
				</p>
			</div>

			<div className="grid w-full max-w-[1400px] grid-cols-1 gap-6 px-0 sm:grid-cols-2 md:px-8 lg:grid-cols-4">
				{filteredData.map((item) => {
					const imageSource = getProductImageUrl(item);
					const style = item?.style || "product";

					return (
						<Link
							to={`/Gowns/id/${style}`}
							state={{ product: item }}
							key={item?._id || item?.id || style}
							aria-label={`View gown style ${style}`}
							className="group relative cursor-pointer overflow-hidden shadow-md focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#8a459f]"
						>
							<img
								src={imageSource}
								alt={`Risky gown style ${style}`}
								
								className="h-[500px] w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
							/>

							<div
								className="absolute inset-0 flex items-center justify-center bg-[#713580]/35 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
								aria-hidden="true"
							>
								<span className="font-poppins text-xl font-semibold tracking-wide text-white">
									{style}
								</span>
							</div>
						</Link>
					);
				})}
			</div>
		</section>
	);
}

export default memo(ExploreCollection);
